import { useEffect, useMemo, useState, useRef } from 'react'
import { useLocation, Link, useNavigate, Navigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { Calendar, Clock, MapPin, User, Phone, ArrowRight, X, CreditCard, CheckCircle2, AlertCircle } from 'lucide-react'
import Terms from './Terms'
import Privacy from './Privacy'
import { db } from '../firebase/config'
import { doc, getDoc, collection, getDocs, addDoc, updateDoc, query as fsQuery, where } from 'firebase/firestore'
import toast from 'react-hot-toast'

const PAYSTACK_PUBLIC_KEY = 'pk_live_d0dac42be7424384d1b9c6dc660d3993efaf5cd6'
const STORAGE_KEY = 'scheduleSettings'

function useQuery() {
  const { search } = useLocation()
  return useMemo(() => new URLSearchParams(search), [search])
}

function toMinutes(hhmm) {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

function overlaps(startA, endA, startB, endB) {
  return startA < endB && startB < endA
}

function getWeekdayName(dateStr) {
  const d = new Date(dateStr)
  return d.toLocaleDateString(undefined, { weekday: 'long' })
}

function pad(n) {
  return String(n).padStart(2, '0')
}

function minutesToTime(mins) {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  // Keep 24-hour format for internal use (consistent with rules)
  return `${pad(h)}:${pad(m)}`
}

const Book = () => {
  const query = useQuery()
  const initialCategory = query.get('category') || ''
  const initialType = query.get('type') || ''
  // Support both old (price) and new (spaPrice/mobilePrice) format
  const initialSpaPrice = query.get('spaPrice') || query.get('price') || ''
  const initialMobilePrice = query.get('mobilePrice') || query.get('price') || ''
  const initialDuration = query.get('duration') || '' // minutes
  const initialVisitType = query.get('visitType') || ''
  const initialPeopleRaw = query.get('people') || ''
  const initialPeople = Math.min(3, Math.max(1, Number(initialPeopleRaw) || 1))
  const fromSessionGiftCard = query.get('fromSessionGiftCard') === 'true'
  const giftCardCodeFromQuery = query.get('giftCardCode') || ''

  const GHANA_REGIONS = [
    'Ahafo', 'Ashanti', 'Bono', 'Bono East', 'Central', 'Eastern',
    'Greater Accra', 'North East', 'Northern', 'Oti', 'Savannah',
    'Upper East', 'Upper West', 'Volta', 'Western', 'Western North'
  ]

  const [settings, setSettings] = useState({
    postBookingIntervalMinutes: 120, 
    rules: [], 
    allowedRegions: [],
    allowedVisitTypes: ['spa', 'mobile'],
    depositSettings: { mode: 'percentage', value: 10 }
  })
  const [promoDiscount, setPromoDiscount] = useState(0) // when > 0 overrides individual service discount
  const [applyPaystackFeeUplift, setApplyPaystackFeeUplift] = useState(true) // when true, add 2% to prices charged/displayed
  const [availableDates, setAvailableDates] = useState([])
  const [availableTimes, setAvailableTimes] = useState([])
  const [regionsLoaded, setRegionsLoaded] = useState(false)
  const [regionsLoading, setRegionsLoading] = useState(false)

  const [form, setForm] = useState({
    name: '',
    phone: '',
    date: '',
    time: '',
    // service details
    category: initialCategory,
    serviceType: initialType,
    spaPrice: initialSpaPrice,
    mobilePrice: initialMobilePrice,
    duration: initialDuration,
    bookingFor: initialPeople, // Number of people (1-3)
    // visit type
    visitType: initialVisitType || '', // 'mobile' | 'spa'
    region: '',
    address: '',
    // payment
    useGiftCard: false,
    payDeposit: true
  })

  const navigate = useNavigate()

  // Redirect to /services when required query params are missing to prevent unprefilled bookings
  useEffect(() => {
    const hasRequired = initialCategory && initialType && initialDuration
    if (!hasRequired) {
      navigate('/services', { replace: true })
    }
  }, [initialCategory, initialType, initialDuration, navigate])

  // Resolve prices and duration from Firestore by category+type so URL cannot be tampered
  useEffect(() => {
    if (!initialCategory || !initialType) return
    let cancelled = false
    const fetchService = async () => {
      try {
        const categoryKey = initialCategory.trim()
        const typeName = initialType.trim()
        let service = null
        if (categoryKey === 'Combo Packages') {
          const combosRef = collection(db, 'comboPackages')
          const q = fsQuery(combosRef, where('isActive', '==', true))
          const snap = await getDocs(q)
          service = snap.docs.map(d => ({ id: d.id, ...d.data() })).find(s => (s.name || '').trim() === typeName) || null
        } else {
          const servicesRef = collection(db, 'services')
          const q = fsQuery(servicesRef, where('isActive', '==', true), where('category', '==', categoryKey))
          const snap = await getDocs(q)
          service = snap.docs.map(d => ({ id: d.id, ...d.data() })).find(s => (s.name || '').trim() === typeName) || null
        }
        if (cancelled || !service) return
        const spa = Number(service.spaPrice ?? service.price ?? service.amount ?? 0) || 0
        const mobile = Number(service.mobilePrice ?? service.price ?? service.amount ?? 0) || 0
        const effectiveDiscount = promoDiscount > 0 ? promoDiscount : (Number(service.discount) || 0)
        const finalSpa = effectiveDiscount > 0 ? spa * (1 - effectiveDiscount / 100) : spa
        const finalMobile = effectiveDiscount > 0 ? mobile * (1 - effectiveDiscount / 100) : mobile
        const duration = String(service.duration ?? initialDuration ?? '')
        setForm(prev => ({
          ...prev,
          spaPrice: finalSpa.toFixed(2),
          mobilePrice: finalMobile.toFixed(2),
          duration
        }))
      } catch (e) {
        console.error('Book: failed to resolve service price from Firestore', e)
      }
    }
    fetchService()
    return () => { cancelled = true }
  }, [initialCategory, initialType, initialDuration, promoDiscount])

  // Base price per person (after discount, before 2% charges)
  const currentPrice = useMemo(() => {
    if (!form.visitType) return ''
    const basePrice = form.visitType === 'spa' 
      ? Number(form.spaPrice) || 0
      : Number(form.mobilePrice) || 0
    return basePrice.toString()
  }, [form.visitType, form.spaPrice, form.mobilePrice])

  // Price per person shown and charged (includes 2% when uplift is on)
  const displayedPricePerPerson = useMemo(() => {
    const base = Number(currentPrice) || 0
    return base * (applyPaystackFeeUplift ? 1.02 : 1)
  }, [currentPrice, applyPaystackFeeUplift])
  const [submitted, setSubmitted] = useState(false)
  const [showGiftCardModal, setShowGiftCardModal] = useState(false)
  const [giftCardNumber, setGiftCardNumber] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [verifiedGiftCard, setVerifiedGiftCard] = useState(null)
  const [giftCardError, setGiftCardError] = useState('')
  const [showBalancePaymentModal, setShowBalancePaymentModal] = useState(false)
  const [balancePaymentInfo, setBalancePaymentInfo] = useState(null)
  const [processingPayment, setProcessingPayment] = useState(false)
  const [depositPaymentReference, setDepositPaymentReference] = useState('')
  const [showCompleteDepositButton, setShowCompleteDepositButton] = useState(false)
  const [processingAgreement, setProcessingAgreement] = useState(false)
  const [isSubmittingBooking, setIsSubmittingBooking] = useState(false)
  const [legalModalType, setLegalModalType] = useState(null) // 'terms' | 'privacy' | null

  // If user came from session gift card validation, prefill gift card number and mark "use gift card"
  useEffect(() => {
    if (fromSessionGiftCard && giftCardCodeFromQuery && !giftCardNumber) {
      const normalized = giftCardCodeFromQuery.replace(/\D/g, '').trim()
      if (normalized.length >= 8) {
        setGiftCardNumber(normalized)
        setForm(prev => ({ ...prev, useGiftCard: true, payDeposit: false }))
      }
    }
  }, [fromSessionGiftCard, giftCardCodeFromQuery, giftCardNumber])

  // Auto-verify session gift card when landed from deep link (prefill + lock visit type & people)
  const sessionCardAutoVerifiedRef = useRef(false)
  useEffect(() => {
    if (!fromSessionGiftCard || !giftCardCodeFromQuery || sessionCardAutoVerifiedRef.current) return
    const normalized = String(giftCardCodeFromQuery).replace(/\D/g, '').trim()
    if (normalized.length < 8) return
    if (!initialCategory || !initialType) return

    let cancelled = false
    const run = async () => {
      try {
        const giftCardsRef = collection(db, 'giftCards')
        const snapshot = await getDocs(giftCardsRef)
        const matchingCards = snapshot.docs.filter(docSnap => {
          const data = docSnap.data()
          const stored = String(data.cardNumber ?? '').trim()
          return stored === normalized || stored === normalized.replace(/^0+/, '')
        })
        if (cancelled || matchingCards.length === 0) return
        const giftCardDoc = matchingCards[0]
        const giftCard = { id: giftCardDoc.id, ...giftCardDoc.data() }
        if (giftCard.type !== 'session') return
        if (giftCard.suspended) return
        if (!giftCard.expiryDate) return
        const expiry = new Date(giftCard.expiryDate)
        if (expiry < new Date()) return
        const bal = Number(giftCard.balance)
        if (!bal || bal <= 0) return
        if (giftCard.sessionCategory !== initialCategory || giftCard.serviceType !== initialType) return
        const allowedPeople = Math.min(3, Math.max(1, Number(giftCard.people) || 1))
        const visitType = (giftCard.visitType === 'mobile' || giftCard.visitType === 'spa') ? giftCard.visitType : (initialVisitType || 'spa')
        if (cancelled) return
        sessionCardAutoVerifiedRef.current = true
        setVerifiedGiftCard(giftCard)
        setForm(prev => ({
          ...prev,
          visitType,
          bookingFor: allowedPeople,
          useGiftCard: true,
          payDeposit: false
        }))
        setGiftCardNumber(normalized)
        toast.success('Session gift card verified. Visit type and number of people are set from your card.')
      } catch (e) {
        console.error('Session gift card auto-verify failed:', e)
      }
    }
    run()
    return () => { cancelled = true }
  }, [fromSessionGiftCard, giftCardCodeFromQuery, initialCategory, initialType, initialVisitType])
  // If required query params are missing, synchronously redirect to /services to avoid flash
  const shouldRedirectToServices = !initialCategory || !initialType || !initialDuration
  if (shouldRedirectToServices) {
    return <Navigate to="/services" replace />
  }

  // Load schedule settings from Firestore (rules and postBookingIntervalMinutes)
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsDocRef = doc(db, 'settings', 'booking')
        const snap = await getDoc(settingsDocRef)
        
        if (snap.exists()) {
          const data = snap.data()
          const promo = data.promoDiscount
          if (typeof promo === 'number' && promo >= 0) setPromoDiscount(promo)
          if (typeof data.applyPaystackFeeUplift === 'boolean') setApplyPaystackFeeUplift(data.applyPaystackFeeUplift)
          setSettings(prev => ({
            ...prev,
            postBookingIntervalMinutes: data.postBookingIntervalMinutes ?? 120,
            rules: Array.isArray(data.rules) ? data.rules : [],
            allowedRegions: Array.isArray(data.allowedRegions) ? data.allowedRegions : (prev.allowedRegions || []),
            allowedVisitTypes: Array.isArray(data.allowedVisitTypes) && data.allowedVisitTypes.length > 0
              ? data.allowedVisitTypes
              : (prev.allowedVisitTypes || ['spa', 'mobile']),
            depositSettings: data.depositSettings ? {
              mode: data.depositSettings.mode === 'fixed' ? 'fixed' : 'percentage',
              value: Number(data.depositSettings.value) || 10
            } : { mode: 'percentage', value: 10 }
          }))
        } else {
          // No Firestore data, use defaults
          setSettings(prev => ({
            ...prev,
            postBookingIntervalMinutes: 120,
            rules: [],
            allowedRegions: prev.allowedRegions || [],
            allowedVisitTypes: prev.allowedVisitTypes || ['spa', 'mobile'],
            depositSettings: { mode: 'percentage', value: 10 }
          }))
        }
      } catch (error) {
        console.error('Failed to load settings from Firestore:', error)
        // Fallback to defaults on error
        setSettings(prev => ({
          ...prev,
          postBookingIntervalMinutes: 120,
          rules: [],
          allowedRegions: prev.allowedRegions || [],
          allowedVisitTypes: prev.allowedVisitTypes || ['spa', 'mobile'],
          depositSettings: { mode: 'percentage', value: 10 }
        }))
      }
    }
    
    loadSettings()
  }, [])

  // Fetch allowed regions from Firestore
  const fetchRegionsFromFirestore = async (forceRefresh = false) => {
    // Don't fetch if already loaded (unless forcing refresh) or currently loading
    if ((regionsLoaded && !forceRefresh) || regionsLoading) {
      return
    }

    setRegionsLoading(true)
    try {
      const settingsDocRef = doc(db, 'settings', 'booking')
      const snap = await getDoc(settingsDocRef)
      if (snap.exists()) {
        const data = snap.data()
        if (Array.isArray(data.allowedRegions)) {
          // Use Firestore data as source of truth - even if empty array
          const regions = data.allowedRegions
          setSettings(prev => ({
            ...prev,
            allowedRegions: regions
          }))
          setRegionsLoaded(true)
        } else {
          // Invalid data format - clear regions
          setSettings(prev => ({ ...prev, allowedRegions: [] }))
          setRegionsLoaded(true)
        }
      } else {
        // Document doesn't exist - no regions available
        setSettings(prev => ({ ...prev, allowedRegions: [] }))
        setRegionsLoaded(true)
      }
    } catch (e) {
      console.error('Failed to fetch allowed regions:', e)
      // On error, clear regions
      setSettings(prev => ({ ...prev, allowedRegions: [] }))
      setRegionsLoaded(true)
    } finally {
      setRegionsLoading(false)
    }
  }

  // Fetch regions when mobile visit type is selected
  useEffect(() => {
    if (form.visitType === 'mobile') {
      fetchRegionsFromFirestore()
    }
  }, [form.visitType])

  // Build available dates (next 30 days) excluding fully blocked days
  // Auto-updates daily by recalculating based on current date
  useEffect(() => {
    const updateDates = () => {
    const days = []
    const today = new Date()
      today.setHours(0, 0, 0, 0) // Reset to start of day
      
    for (let i = 0; i < 30; i++) {
      const d = new Date(today)
      d.setDate(today.getDate() + i)
      const iso = d.toISOString().slice(0, 10)
      const weekday = d.toLocaleDateString(undefined, { weekday: 'long' })

      // Determine if the day is fully blocked by a rule spanning full day
      const fullDayBlocked = settings.rules.some((r) => {
        if (r.type === 'one_off' && r.date === iso) {
          return r.startTime === '00:00' && r.endTime === '23:59'
        }
        if (r.type === 'weekly' && r.weekday === weekday) {
          return r.startTime === '00:00' && r.endTime === '23:59'
        }
        if (r.type === 'monthly' && Number(r.dayOfMonth) === d.getDate()) {
          return r.startTime === '00:00' && r.endTime === '23:59'
        }
          if (r.type === 'daily') {
            return r.startTime === '00:00' && r.endTime === '23:59'
          }
        return false
      })

      if (!fullDayBlocked) days.push({ label: d.toDateString(), value: iso })
    }
    setAvailableDates(days)
    if (!form.date && days.length > 0) {
      setForm((prev) => ({ ...prev, date: days[0].value }))
    }
    }
    
    updateDates()
    // Update dates daily - set up interval to check at midnight
    const interval = setInterval(() => {
      updateDates()
    }, 60000) // Check every minute (could optimize to check once per day)
    
    return () => clearInterval(interval)
  }, [settings.rules, form.date])

  // Build available times when date or duration changes
  useEffect(() => {
    if (!form.date) {
      setAvailableTimes([])
      return
    }
    
    const baseDuration = Number(form.duration) || 60
    const bookingFor = Number(form.bookingFor) || 1
    // Duration scaling: base + 30 minutes per additional person
    const durationMin = baseDuration + (bookingFor - 1) * 30
    const buffer = Number(settings.postBookingIntervalMinutes) || 0

    const opening = 0 * 60 // 00:00 (midnight) - all 24 hours
    const closing = 24 * 60 // 24:00 (midnight) - all 24 hours available

    const weekday = getWeekdayName(form.date)

    // Collect blocked intervals for the given date
    const blocked = []
    
    // Add unavailability rules
    settings.rules.forEach((r) => {
      if (!r.startTime || !r.endTime) return
      
      const startMinutes = toMinutes(r.startTime)
      const endMinutes = toMinutes(r.endTime)
      
      // Handle rules that span midnight (e.g., 18:00 to 06:00)
      const spansMidnight = startMinutes > endMinutes
      
      if (r.type === 'one_off' && r.date === form.date) {
        if (spansMidnight) {
          // Split into two intervals: [start to 24:00] and [00:00 to end]
          blocked.push([startMinutes, 24 * 60]) // Until midnight
          blocked.push([0, endMinutes]) // From midnight to end
        } else {
          blocked.push([startMinutes, endMinutes])
        }
      } else if (r.type === 'weekly' && r.weekday === weekday) {
        if (spansMidnight) {
          blocked.push([startMinutes, 24 * 60])
          blocked.push([0, endMinutes])
        } else {
          blocked.push([startMinutes, endMinutes])
        }
      } else if (r.type === 'monthly' && Number(r.dayOfMonth) === new Date(form.date).getDate()) {
        if (spansMidnight) {
          blocked.push([startMinutes, 24 * 60])
          blocked.push([0, endMinutes])
        } else {
          blocked.push([startMinutes, endMinutes])
        }
      } else if (r.type === 'daily') {
        // Daily rules apply to all dates
        if (spansMidnight) {
          blocked.push([startMinutes, 24 * 60])
          blocked.push([0, endMinutes])
        } else {
          blocked.push([startMinutes, endMinutes])
        }
      }
    })
    
    // Fetch existing bookings for this date and add to blocked intervals
    const fetchAndBlockBookings = async () => {
      try {
        const bookingsRef = collection(db, 'bookings')
        const snapshot = await getDocs(bookingsRef)
        
        // Filter bookings for the selected date client-side
        snapshot.docs.forEach((docSnap) => {
          const booking = docSnap.data()
          
          // Only process bookings for the selected date
          if (booking.date === form.date) {
            // Block upcoming appointments (pending and rescheduled)
            // Don't block completed (they're done) or cancelled (they're not happening)
            const isUpcoming = booking.status === 'pending' || booking.status === 'rescheduled'
            
            if (isUpcoming) {
              const bookingStart = toMinutes(booking.time)
              const bookingDuration = Number(booking.duration) || durationMin
              // Calculate: booking start time + booking duration + post-booking interval
              // This ensures there's a buffer after each booking before the next one can start
              // Example: If booking is 2:00 PM, duration 60min, buffer 120min
              // Then block from 2:00 PM to 5:00 PM (2:00 + 60min + 120min = 5:00 PM)
              const bookingEnd = bookingStart + bookingDuration + buffer
              
              // Block the entire interval from booking start to booking end + buffer
              blocked.push([bookingStart, bookingEnd])
            }
          }
        })
        
        // Generate all possible time slots at 30-minute intervals
        // This allows showing all times like 12:00 AM, 12:30 AM, 1:00 AM, 1:30 AM, etc.
        const timeInterval = 30 // 30-minute intervals
    const times = []
        
        // Get current time for today to filter out past times
        const today = new Date().toISOString().slice(0, 10)
        const isToday = form.date === today
        const now = new Date()
        const currentMinutes = isToday ? now.getHours() * 60 + now.getMinutes() : 0
        
        for (let start = opening; start + durationMin <= closing; start += timeInterval) {
      const end = start + durationMin
          
          // Filter out past times for today
          if (isToday && start < currentMinutes) {
            continue
          }
          
          // Check if this time slot overlaps with any blocked interval
      const isBlocked = blocked.some(([bStart, bEnd]) => overlaps(start, end, bStart, bEnd))
          
          if (!isBlocked) {
            times.push(minutesToTime(start))
          }
    }

    setAvailableTimes(times)
        // Check if currently selected time is still available for this date
        const currentTimeAvailable = form.time && times.includes(form.time)
        
        if (times.length > 0) {
          if (!form.time || !currentTimeAvailable) {
            // No time selected or current time not available - auto-select first available time
            setForm((prev) => ({ ...prev, time: times[0] }))
          }
          // If current time is available, keep it
        } else {
          // No available times - clear selection
          setForm((prev) => ({ ...prev, time: '' }))
        }
      } catch (error) {
        console.error('Error fetching bookings for date:', error)
        // If query fails, still generate times without booking blocks
        const timeInterval = 30
        const times = []
        
        // Get current time for today to filter out past times
        const today = new Date().toISOString().slice(0, 10)
        const isToday = form.date === today
        const now = new Date()
        const currentMinutes = isToday ? now.getHours() * 60 + now.getMinutes() : 0
        
        for (let start = opening; start + durationMin <= closing; start += timeInterval) {
          const end = start + durationMin
          
          // Filter out past times for today
          if (isToday && start < currentMinutes) {
            continue
          }
          
          const isBlocked = blocked.some(([bStart, bEnd]) => overlaps(start, end, bStart, bEnd))
          
          if (!isBlocked) {
            times.push(minutesToTime(start))
          }
        }
        
        setAvailableTimes(times)
        // Check if currently selected time is still available for this date
        const currentTimeAvailable = form.time && times.includes(form.time)
        
        if (times.length > 0) {
          if (!form.time || !currentTimeAvailable) {
            // No time selected or current time not available - auto-select first available time
            setForm((prev) => ({ ...prev, time: times[0] }))
          }
          // If current time is available, keep it
        } else {
          // No available times - clear selection
          setForm((prev) => ({ ...prev, time: '' }))
        }
      }
    }
    
    fetchAndBlockBookings()
  }, [form.date, form.duration, form.bookingFor, settings.postBookingIntervalMinutes, settings.rules])

  const depositAmount = useMemo(() => {
    const bookingFor = Number(form.bookingFor) || 1
    const totalDisplayed = displayedPricePerPerson * bookingFor
    const mode = settings.depositSettings?.mode || 'percentage'
    const value = Number(settings.depositSettings?.value) || 10
    
    if (mode === 'fixed') return value.toFixed(2)
    return ((totalDisplayed * value) / 100).toFixed(2)
  }, [displayedPricePerPerson, form.bookingFor, settings.depositSettings])

  // Ghana Regions list (16) moved to top of file

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    if (type === 'checkbox') {
      if (name === 'useGiftCard' && checked) {
        // If using gift card, open verification modal
        setShowGiftCardModal(true)
        setForm(prev => ({ ...prev, useGiftCard: true, payDeposit: false }))
      } else if (name === 'payDeposit' && checked) {
        // If paying deposit, disable gift card
        setVerifiedGiftCard(null)
        setForm(prev => ({ ...prev, payDeposit: true, useGiftCard: false }))
      } else {
        // Unchecking either one doesn't auto-check the other
        setForm(prev => ({ ...prev, [name]: checked }))
        if (name === 'useGiftCard' && !checked) {
          setVerifiedGiftCard(null)
          setGiftCardNumber('')
        }
      }
    } else {
      // If date changes, clear the time so user can select a new time for the new date
      if (name === 'date') {
        setForm(prev => ({ ...prev, [name]: value, time: '' }))
      } else {
        setForm(prev => ({ ...prev, [name]: value }))
      }
    }
  }

  // Verify gift card
  const verifyGiftCard = async () => {
    // Validate card number (12 digits)
    const cardNum = giftCardNumber.replace(/\D/g, '') // Remove non-digits
    if (cardNum.length !== 12) {
      setGiftCardError('Card number must be 12 digits')
      return
    }

    setVerifying(true)
    setGiftCardError('')

    try {
      // Query gift cards by cardNumber
      const giftCardsRef = collection(db, 'giftCards')
      const snapshot = await getDocs(giftCardsRef)

      // Filter gift cards by cardNumber client-side
      const matchingCards = snapshot.docs.filter(doc => {
        const data = doc.data()
        return data.cardNumber === cardNum
      })

      if (matchingCards.length === 0) {
        setGiftCardError('Gift card not found. Please check the number and try again.')
        setVerifying(false)
        return
      }

      const giftCardDoc = matchingCards[0]
      const giftCard = { id: giftCardDoc.id, ...giftCardDoc.data() }

      // Check if card is suspended
      if (giftCard.suspended) {
        setGiftCardError('This gift card has been suspended.')
        setVerifying(false)
        return
      }

      // Check expiry date (mandatory check - all gift cards should have expiry dates)
      if (!giftCard.expiryDate) {
        setGiftCardError('This gift card is missing expiry information. Please contact support.')
        setVerifying(false)
        return
      }

      // Verify the card hasn't expired
      const expiry = new Date(giftCard.expiryDate)
      const now = new Date()
      // Compare dates (ignore time component) - card is valid until end of expiry date
      if (expiry < now) {
        const expiryDateStr = expiry.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })
        setGiftCardError(`This gift card expired on ${expiryDateStr}.`)
        setVerifying(false)
        return
      }

      // Check card type and validate
      if (giftCard.type === 'session') {
        // Check if balance is not 0 (not used)
        if (!giftCard.balance || giftCard.balance === 0) {
          setGiftCardError('This session gift card has already been used.')
          setVerifying(false)
          return
        }

        // Check if service matches session card
        if (giftCard.sessionCategory !== form.category || giftCard.serviceType !== form.serviceType) {
          setGiftCardError(`This gift card is for ${giftCard.sessionCategory} - ${giftCard.serviceType}. Please visit that service page to book.`)
          setVerifying(false)
          return
        }

        // Check if visitType is selected (required for session cards)
        if (!form.visitType) {
          setGiftCardError('Please select visit type (Spa or Mobile) first.')
          setVerifying(false)
          return
        }

        // If card has a specific visitType, enforce it
        if (giftCard.visitType && form.visitType !== giftCard.visitType) {
          setGiftCardError(`This gift card is for a ${giftCard.visitType === 'mobile' ? 'Mobile Spa' : 'Spa'} visit. Please choose that visit type to use this card.`)
          setVerifying(false)
          return
        }

        // Session cards are validated by existence, expiry, suspension and service match only.
        // Do NOT compare stored price with current prices; session cards are tied to a specific
        // service regardless of later price changes.
        // Enforce bookingFor and visitType from card
        const allowedPeople = Number(giftCard.people) || 1
        setVerifiedGiftCard(giftCard)
        setForm(prev => ({
          ...prev,
          bookingFor: allowedPeople,
          visitType: giftCard.visitType || prev.visitType
        }))
        setShowGiftCardModal(false)
        toast.success(`Gift card verified! Booking will be for ${allowedPeople} person${allowedPeople > 1 ? 's' : ''} as set on the gift card.`)
      } else if (giftCard.type === 'wellness') {
        // Check if balance covers the total service price (displayed price including 2% when uplift on)
        const bookingFor = Number(form.bookingFor) || 1
        const servicePrice = displayedPricePerPerson * bookingFor
        const cardBalance = Number(giftCard.balance) || 0

        if (cardBalance >= servicePrice) {
          // Balance covers service
          setVerifiedGiftCard(giftCard)
          setShowGiftCardModal(false)
          toast.success('Gift card verified! Full amount covered.')
        } else {
          // Balance doesn't cover - show styled modal to ask user to pay balance
          const balance = servicePrice - cardBalance
          setBalancePaymentInfo({
            cardBalance,
            servicePrice,
            balance,
            giftCard
          })
          setShowGiftCardModal(false)
          setShowBalancePaymentModal(true)
          setVerifying(false)
          return
        }
      }

      setVerifying(false)
    } catch (error) {
      console.error('Error verifying gift card:', error)
      setGiftCardError('Failed to verify gift card. Please try again.')
      setVerifying(false)
    }
  }

  // Helper function to complete booking (saves to Firestore and sends SMS)
  const completeBooking = async (servicePrice, deposit, balance, bookingFor, adjustedDuration, depositTransactionRef = '') => {
    try {
      // Defensive: ensure session gift cards always create bookings that match the card's people setting
      let finalBookingFor = bookingFor
      if (verifiedGiftCard && verifiedGiftCard.type === 'session') {
        const allowedPeople = Number(verifiedGiftCard.people) || 1
        finalBookingFor = allowedPeople
      }

      // servicePrice passed in is already the displayed total (includes 2% when uplift on)

      // Prepare booking data
      const bookingData = {
        name: form.name,
        phone: form.phone,
        category: form.category,
        serviceType: form.serviceType,
        price: servicePrice,
        duration: adjustedDuration,
        bookingFor: finalBookingFor, // Number of people
        date: form.date,
        time: form.time,
        visitType: form.visitType,
        region: form.visitType === 'mobile' ? form.region : '',
        address: form.visitType === 'mobile' ? form.address : '',
        deposit: deposit,
        deposited: deposit > 0,
        depositTransactionRef: depositTransactionRef || '',
        balance: balance,
        balancePaid: balance === 0,
        balanceTransactionRef: '',
        paymentCompleted: balance === 0,
        useGiftCard: form.useGiftCard,
        payDeposit: form.payDeposit,
        giftCardId: verifiedGiftCard?.id || '',
        giftCardNumber: verifiedGiftCard?.cardNumber || '',
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      // Save booking to Firestore
      const bookingsRef = collection(db, 'bookings')
      const bookingRef = await addDoc(bookingsRef, bookingData)

      // Update gift card balance
      if (form.useGiftCard && verifiedGiftCard) {
        const giftCardRef = doc(db, 'giftCards', verifiedGiftCard.id)
        
        if (verifiedGiftCard.type === 'session') {
          // Session card: set balance to 0 (used) and automatically suspend
          await updateDoc(giftCardRef, {
            balance: 0,
            suspended: true, // Automatically suspend when balance reaches 0
            usedAt: new Date().toISOString(),
            bookingId: bookingRef.id,
            updatedAt: new Date().toISOString()
          })
        } else if (verifiedGiftCard.type === 'wellness') {
          // Wellness card: deduct total booking amount (servicePrice parameter) from balance
          const cardBalance = Number(verifiedGiftCard.balance) || 0
          const newBalance = Math.max(0, cardBalance - servicePrice)
          
          // Automatically suspend if balance reaches 0
          const updateData = {
            balance: newBalance,
            lastUsedAt: new Date().toISOString(),
            lastBookingId: bookingRef.id,
            updatedAt: new Date().toISOString()
          }
          
          if (newBalance === 0) {
            updateData.suspended = true
          }
          
          await updateDoc(giftCardRef, updateData)
        }
      }

      // Send SMS notifications and schedule reminders
      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions')
        const { getApp } = await import('firebase/app')
        const app = getApp()
        const functionsInstance = getFunctions(app)
        
        const completeFlow = httpsCallable(functionsInstance, 'completeBookingFlow')
        await completeFlow({
          bookingId: bookingRef.id,
          name: form.name,
          phone: form.phone,
          date: form.date,
          time: form.time,
          serviceType: form.serviceType,
          category: form.category,
          visitType: form.visitType,
          address: form.visitType === 'mobile' ? form.address : undefined,
          useGiftCard: form.useGiftCard || false,
          giftCardNumber: verifiedGiftCard?.cardNumber || undefined,
          giftCardType: verifiedGiftCard?.type || undefined,
          balance: balance > 0 ? balance : undefined
        })
      } catch (smsError) {
        console.error('Error sending SMS notifications:', smsError)
        // Don't fail the booking if SMS fails
      }

      toast.success('Booking confirmed successfully!')
    setSubmitted(true)
      setIsSubmittingBooking(false)
    } catch (error) {
      console.error('Error saving booking:', error)
      toast.error('Failed to save booking. Please try again.')
      setIsSubmittingBooking(false)
      throw error
    }
  }

  // Handle deposit payment with Paystack inline (client-side)
  const handleDepositPayment = async (deposit, servicePrice, bookingFor, adjustedDuration) => {
    try {
      setProcessingPayment(true)

      // Build a safe email from phone for Paystack
      const digits = (form.phone || '').replace(/\D/g, '')
      const customerEmail = (digits || 'customer') + '@temp.sparadiseluxe.com'

      const startPayment = () => {
        if (!window.PaystackPop) {
          toast.error('Payment system not available. Please check your connection and try again.')
          setProcessingPayment(false)
          return
        }

        const handler = window.PaystackPop.setup({
          key: PAYSTACK_PUBLIC_KEY,
          email: customerEmail,
          amount: Math.round(deposit * 100), // GHS → pesewas
          currency: 'GHS',
          ref: `BOOKING_${Date.now()}`,
          metadata: {
            bookingName: form.name,
            bookingPhone: form.phone,
            serviceType: form.serviceType,
            category: form.category,
          },
          callback: function (response) {
            // Payment successful: verify and complete booking
            setProcessingPayment(false)
            const ref = response.reference
            setDepositPaymentReference(ref)
            handleCompleteDepositPayment(ref)
          },
          onClose: function () {
            setProcessingPayment(false)
            toast.error('Payment was cancelled. You can start a new booking.')
          },
        })

        handler.openIframe()
      }

      // If script already loaded, start immediately
      if (window.PaystackPop) {
        startPayment()
        return
      }

      // Otherwise, load Paystack inline script (v1) then start
      const script = document.createElement('script')
      script.src = 'https://js.paystack.co/v1/inline.js'
      script.onload = startPayment
      script.onerror = () => {
        toast.error('Failed to load payment gateway. Please try again.')
        setProcessingPayment(false)
      }
      document.body.appendChild(script)
    } catch (error) {
      console.error('Error starting deposit payment:', error)
      toast.error('An error occurred. Please try again.')
      setProcessingPayment(false)
    }
  }

  // Force reset payment states - call this to clear any stuck loading
  const resetPaymentStates = () => {
    setProcessingPayment(false)
    setShowCompleteDepositButton(false)
    setIsSubmittingBooking(false)
    // Don't clear depositPaymentReference here - we might need it for retry
  }

  // Manual verification handler - called when user clicks "Confirm Payment/Booking" button
  const handleCompleteDepositPayment = async (referenceOverride = null) => {
    // Get current reference - use override if provided (from button click), otherwise use state
    const currentReference = referenceOverride || depositPaymentReference
    
    if (!currentReference) {
      resetPaymentStates()
      toast.error('Payment reference not found. Please try again.')
      return
    }

    // Start verification process
    setProcessingPayment(true)
    setShowCompleteDepositButton(false)

    try {
      // Import Firebase Functions dynamically
      const { getFunctions, httpsCallable } = await import('firebase/functions')
      const { getApp } = await import('firebase/app')
      const app = getApp()
      const functionsInstance = getFunctions(app)

      // Show verification notice
      const verificationToast = toast.loading('Verifying payment status...', {
        duration: 5000,
      })

      // Wait a moment for Paystack to update transaction status
      await new Promise(resolve => setTimeout(resolve, 2000))

      // Verify payment status with Paystack using the captured reference
      const verifyPayment = httpsCallable(functionsInstance, 'verifyBookingDepositPayment')
      const verifyResult = await verifyPayment({
        reference: currentReference
      })

      const verifyData = verifyResult.data

      toast.dismiss(verificationToast)

      if (!verifyData || !verifyData.success) {
        // Payment not successful or abandoned
        setProcessingPayment(false)
        setShowCompleteDepositButton(true) // Re-show button to allow retry
        // Keep depositPaymentReference so user can retry
        
        // Check if payment was cancelled/abandoned vs failed
        const paymentStatus = verifyData?.status || 'unknown'
        if (paymentStatus === 'abandoned' || paymentStatus === 'cancelled') {
          toast.error('Payment was cancelled. Please try again.', {
            duration: 5000,
          })
          // Clear reference if abandoned - user needs to start fresh
          setDepositPaymentReference('')
          setShowCompleteDepositButton(false)
        } else {
          toast.error('Payment verification failed. Please try again.', {
            duration: 5000,
          })
        }
        return
      }

      // Calculate booking details (use displayed price including 2% when uplift on)
      let bookingFor = Number(form.bookingFor) || 1
      if (verifiedGiftCard && verifiedGiftCard.type === 'session') {
        const allowedPeople = Number(verifiedGiftCard.people) || 1
        bookingFor = allowedPeople
      }
      const servicePrice = displayedPricePerPerson * bookingFor
      const deposit = Number(depositAmount)
      const balance = servicePrice - deposit

      const baseDuration = Number(form.duration) || 60
      const adjustedDuration = baseDuration + (bookingFor - 1) * 30

      // Complete booking with payment reference
      await completeBooking(
        servicePrice,
        deposit,
        balance,
        bookingFor,
        adjustedDuration,
        verifyData.reference
      )

      setProcessingPayment(false)
      setShowCompleteDepositButton(false)
      setDepositPaymentReference('')
    } catch (error) {
      console.error('Error verifying deposit payment:', error)
      setProcessingPayment(false)
      setShowCompleteDepositButton(true) // Re-show button to allow retry
      // Keep reference in case it's a network error and payment actually succeeded
      
      toast.error('Failed to verify payment. Please try again or contact support.', {
        duration: 5000,
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Prevent multiple submissions
    if (isSubmittingBooking) {
      return
    }
    
    // Validate that available times exist for the selected date
    if (availableTimes.length === 0) {
      toast.error('No available times for the selected date. Please choose another date.', {
        duration: 5000,
        icon: '⚠️',
      })
      // Scroll to the date field to prompt user to change date
      const dateField = document.querySelector('select[name="date"]')
      if (dateField) {
        dateField.scrollIntoView({ behavior: 'smooth', block: 'center' })
        dateField.focus()
        // Highlight the field briefly
        dateField.style.borderColor = '#ef4444'
        dateField.style.borderWidth = '2px'
        setTimeout(() => {
          dateField.style.borderColor = ''
          dateField.style.borderWidth = ''
        }, 3000)
      }
      return
    }
    
    // Validate that a time is selected
    if (!form.time || form.time === '') {
      toast.error('Please select an available time for your booking', {
        duration: 5000,
        icon: '⏰',
      })
      // Scroll to the time field to prompt user to select time
      const timeField = document.querySelector('select[name="time"]')
      if (timeField) {
        timeField.scrollIntoView({ behavior: 'smooth', block: 'center' })
        timeField.focus()
        // Highlight the field briefly
        timeField.style.borderColor = '#ef4444'
        timeField.style.borderWidth = '2px'
        setTimeout(() => {
          timeField.style.borderColor = ''
          timeField.style.borderWidth = ''
        }, 3000)
      }
      return
    }
    
    // If using gift card, must be verified
    if (form.useGiftCard && !verifiedGiftCard) {
      toast.error('Please verify your gift card first')
      setShowGiftCardModal(true)
      return
    }

    // Require a payment option: either pay deposit now or use a gift card
    if (!form.payDeposit && !form.useGiftCard) {
      toast.error('Please select a payment option: pay deposit now or use a gift card.')
      // Scroll to payment options UI so user can select
      const paymentSection = document.querySelector('.spa-section')
      if (paymentSection) {
        const paymentOptions = paymentSection.querySelector('input[name="useGiftCard"], input[name="payDeposit"]')
        if (paymentOptions && paymentOptions.scrollIntoView) {
          paymentOptions.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
      return
    }

    try {
      setIsSubmittingBooking(true)
      // Calculate adjusted price and duration based on bookingFor (use displayed price including 2% when uplift on)
      let bookingFor = Number(form.bookingFor) || 1
      if (verifiedGiftCard && verifiedGiftCard.type === 'session') {
        const allowedPeople = Number(verifiedGiftCard.people) || 1
        bookingFor = allowedPeople
      }
      const servicePrice = displayedPricePerPerson * bookingFor

      const baseDuration = Number(form.duration) || 60
      const adjustedDuration = baseDuration + (bookingFor - 1) * 30
      
      let deposit = 0
      let balance = 0

      if (form.useGiftCard && verifiedGiftCard) {
        if (verifiedGiftCard.type === 'session') {
          deposit = 0
          balance = 0
        } else if (verifiedGiftCard.type === 'wellness') {
          const cardBalance = Number(verifiedGiftCard.balance) || 0
          if (cardBalance >= servicePrice) {
            deposit = 0
            balance = 0
          } else {
            deposit = 0
            balance = verifiedGiftCard.balanceToPay || (servicePrice - cardBalance)
          }
        }
      } else if (form.payDeposit) {
        deposit = Number(depositAmount)
        balance = servicePrice - deposit
        
        // If paying deposit, initialize PayStack payment
        if (deposit > 0) {
          try {
            await handleDepositPayment(deposit, servicePrice, bookingFor, adjustedDuration)
            // Reset submitting state after payment popup opens
            setIsSubmittingBooking(false)
          } catch (error) {
            console.error('Error in deposit payment:', error)
            setIsSubmittingBooking(false)
            setProcessingPayment(false)
            toast.error('Failed to initialize payment. Please try again.')
          }
          return // Payment handler will complete the booking
        }
      } else {
        balance = servicePrice
      }

      // If no deposit payment needed, proceed with booking
      await completeBooking(servicePrice, deposit, balance, bookingFor, adjustedDuration)
    } catch (error) {
      console.error('Error in booking submission:', error)
      // Error already handled in completeBooking
      setIsSubmittingBooking(false)
    }
  }

  return (
    <>
    <div className="spa-section min-h-screen bg-gradient-to-br from-purple-50/50 via-white to-pink-50/50">
      <div className="container-spa max-w-4xl mx-auto py-12 px-4">
        {/* Centered Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mb-6 shadow-lg">
            <Calendar className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-800 mb-4">Book a Service</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">Select an available date and time. Duration and deposit are auto-calculated.</p>
        </div>

        <div className="card bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-purple-100">
            {submitted ? (
              <div className="text-center py-16">
                <h2 className="text-2xl font-semibold text-purple-700 mb-2">Booking Confirmed!</h2>
                <p className="text-gray-600 mb-6">We will contact you if any adjustments are needed.</p>
                <Link to="/services" className="inline-flex items-center space-x-2 text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 px-5 py-3 rounded-lg shadow-md">
                  <span>Back to Services</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Customer Details Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
                    <User className="w-5 h-5 text-purple-500" />
                    <span>Customer Details</span>
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Name <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <User className="w-5 h-5 text-gray-400 absolute left-3 top-3.5" />
                        <input
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all"
                          placeholder="Your full name"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Phone className="w-5 h-5 text-gray-400 absolute left-3 top-3.5" />
                        <input
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all"
                          placeholder="024 XXX XXXX"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Visit Type */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Visit Type <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <MapPin className="w-5 h-5 text-gray-400 absolute left-3 top-3.5" />
                          <select
                            name="visitType"
                            value={form.visitType}
                            onChange={handleChange}
                            className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                            required
                            disabled={verifiedGiftCard?.type === 'session' || (fromSessionGiftCard && form.useGiftCard && giftCardNumber.replace(/\D/g, '').length >= 8)}
                          >
                            <option value="">Select visit type</option>
                            {settings.allowedVisitTypes.includes('mobile') && (
                              <option value="mobile">Mobile Spa (come to me)</option>
                            )}
                            {settings.allowedVisitTypes.includes('spa') && (
                              <option value="spa">Spa (I will come to your shop)</option>
                            )}
                          </select>
                        {(verifiedGiftCard?.type === 'session' || (fromSessionGiftCard && form.useGiftCard && giftCardNumber.replace(/\D/g, '').length >= 8)) && (
                          <p className="text-xs text-amber-700 mt-1">Visit type is locked to match your session gift card.</p>
                        )}
                      </div>
                    </div>

                    {/* Region - only for mobile */}
                    {form.visitType === 'mobile' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Region <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <MapPin className="w-5 h-5 text-gray-400 absolute left-3 top-3.5" />
                            <select
                              name="region"
                              value={form.region}
                              onChange={handleChange}
                              onFocus={() => {
                                if (!regionsLoaded && !regionsLoading) {
                                  fetchRegionsFromFirestore(true)
                                }
                              }}
                              className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all bg-white"
                              required={form.visitType === 'mobile'}
                              disabled={regionsLoading}
                              key={`region-select-${settings.allowedRegions?.length || 0}-${regionsLoaded}`}
                            >
                              <option value="">
                                {regionsLoading 
                                  ? 'Loading regions...' 
                                  : settings.allowedRegions && settings.allowedRegions.length > 0
                                    ? 'Select region'
                                    : !regionsLoaded
                                      ? 'Click to load regions'
                                      : 'No regions available'}
                              </option>
                              {settings.allowedRegions && Array.isArray(settings.allowedRegions) && settings.allowedRegions.length > 0 && (
                                settings.allowedRegions.map((r) => (
                                  <option key={r} value={r}>{r}</option>
                                ))
                              )}
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Address <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <MapPin className="w-5 h-5 text-gray-400 absolute left-3 top-3.5" />
                            <input
                              name="address"
                              value={form.address}
                              onChange={handleChange}
                              className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all"
                              placeholder="Area/City (e.g., East Legon, Adjiringanor)"
                              required={form.visitType === 'mobile'}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Scheduling Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
                    <Calendar className="w-5 h-5 text-blue-500" />
                    <span>Schedule</span>
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Preferred Date <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Calendar className="w-5 h-5 text-gray-400 absolute left-3 top-3.5 pointer-events-none" />
                        <select 
                          name="date" 
                          value={form.date} 
                          onChange={handleChange} 
                          className="w-full pl-11 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all appearance-none bg-white"
                          required
                        >
                          {availableDates.map((d) => (
                            <option key={d.value} value={d.value}>{d.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Available Time <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <Clock className="w-5 h-5 text-gray-400 absolute left-3 top-3.5 pointer-events-none" />
                        <select 
                          name="time" 
                          value={form.time} 
                          onChange={handleChange} 
                          className={`w-full pl-11 pr-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all appearance-none bg-white ${
                            availableTimes.length === 0 
                              ? 'border-amber-300 bg-amber-50 text-amber-800' 
                              : 'border-gray-300'
                          }`}
                          required
                          disabled={availableTimes.length === 0}
                        >
                          {availableTimes.length === 0 && (
                            <option value="">All times booked - Try another date</option>
                          )}
                          {availableTimes.map((t) => {
                            // Convert 24-hour format (HH:MM) to display format (H:MM AM/PM)
                            const [h, m] = t.split(':').map(Number)
                            let hour12, ampm
                            if (h === 0 || h === 24) {
                              hour12 = 12
                              ampm = 'AM'
                            } else if (h === 12) {
                              hour12 = 12
                              ampm = 'PM'
                            } else if (h > 12) {
                              hour12 = h - 12
                              ampm = 'PM'
                            } else {
                              hour12 = h
                              ampm = 'AM'
                            }
                            const displayTime = `${hour12}:${String(m).padStart(2, '0')} ${ampm}`
                            return <option key={t} value={t}>{displayTime}</option>
                          })}
                        </select>
                      </div>
                      {availableTimes.length === 0 && (
                        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm text-amber-800 flex items-start">
                            <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                            <span>
                              <strong>This date is fully booked.</strong> Please select another date from the dropdown above to see available times.
                            </span>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Service Details Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
                    <Clock className="w-5 h-5 text-amber-500" />
                    <span>Service Details</span>
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                      <input 
                        name="category" 
                        value={form.category} 
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed" 
                        readOnly 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Service Type</label>
                      <input 
                        name="serviceType" 
                        value={form.serviceType} 
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed" 
                        readOnly 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Base Price</label>
                      <input 
                        name="price"
                        value={currentPrice ? `GHS ${displayedPricePerPerson.toFixed(2)}` : ''}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed" 
                        readOnly 
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Booking For <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="bookingFor"
                        value={form.bookingFor}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all bg-white"
                        required
                        disabled={Boolean(verifiedGiftCard) || (fromSessionGiftCard && form.useGiftCard && giftCardNumber.replace(/\D/g, '').length >= 8)}
                      >
                        <option value="1">1 Person</option>
                        <option value="2">2 People</option>
                        <option value="3">3 People</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Total: GHS {(displayedPricePerPerson * (Number(form.bookingFor) || 1)).toFixed(2)} 
                        {Number(form.bookingFor) > 1 && ` (GHS ${displayedPricePerPerson.toFixed(2)} × ${form.bookingFor})`}
                        {(verifiedGiftCard || (fromSessionGiftCard && form.useGiftCard && giftCardNumber.replace(/\D/g, '').length >= 8)) && (
                          <span className="block text-xs text-amber-700 mt-1">Booking for is locked because a gift card was verified.</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        Duration: {((Number(form.duration) || 60) + ((Number(form.bookingFor) || 1) - 1) * 30)} minutes
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Total Price</label>
                      <input 
                        value={`GHS ${(displayedPricePerPerson * (Number(form.bookingFor) || 1)).toFixed(2)}`} 
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed" 
                        readOnly 
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {Number(form.bookingFor) > 1
                          ? `GHS ${displayedPricePerPerson.toFixed(2)} × ${form.bookingFor} people`
                          : applyPaystackFeeUplift ? '(includes 2% charges fee)' : 'Price as shown'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Deposit Amount</label>
                      <input 
                        value={`GHS ${depositAmount}`} 
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed" 
                        readOnly 
                      />
                    </div>
                  </div>
                  
                  {/* Deposit Notice */}
                  {form.payDeposit && Number(depositAmount) > 0 && (
                    <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-amber-800">
                            <strong>Important:</strong> Deposit payments are <strong>non-refundable</strong> if you cancel your booking. However, you may contact us to reschedule your appointment. Please review our{' '}
                            <button
                              type="button"
                              onClick={() => setLegalModalType('terms')}
                              className="text-amber-900 underline font-semibold hover:text-amber-950"
                            >
                              Terms of Service
                            </button>{' '}
                            for more details.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Payment Options Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
                    <ArrowRight className="w-5 h-5 text-purple-500" />
                    <span>Payment Options</span>
                  </h3>
                  
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div
                      className={`flex items-center space-x-2 p-4 border-2 rounded-lg transition-all cursor-pointer select-none ${
                        verifiedGiftCard 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-300 hover:border-green-500'
                      }`}
                      onClick={() => {
                        if (!form.useGiftCard) {
                          setShowGiftCardModal(true)
                          setForm(prev => ({ ...prev, useGiftCard: true, payDeposit: false }))
                        } else {
                          setVerifiedGiftCard(null)
                          setGiftCardNumber('')
                          setForm(prev => ({ ...prev, useGiftCard: false }))
                        }
                      }}
                    >
                      <input 
                        type="checkbox" 
                        name="useGiftCard" 
                        checked={form.useGiftCard} 
                        onChange={handleChange}
                        className="w-5 h-5 text-purple-500 rounded focus:ring-2 focus:ring-purple-400 pointer-events-auto"
                      />
                      <div className="flex-1">
                      <label className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                        I have a gift card
                      </label>
                        {verifiedGiftCard && (
                          <div className="flex items-center space-x-1 mt-1">
                            <CheckCircle2 className="w-3 h-3 text-green-600" />
                            <span className="text-xs text-green-600">
                              {verifiedGiftCard.type === 'session' 
                                ? 'Session card verified'
                                : `Balance: GHS ${Number(verifiedGiftCard.balance || 0).toFixed(2)}${verifiedGiftCard.balanceToPay ? ` (+ GHS ${verifiedGiftCard.balanceToPay.toFixed(2)} balance)` : ''}`
                              }
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div
                      className="flex items-center space-x-2 p-4 border-2 border-gray-300 rounded-lg hover:border-green-500 transition-all cursor-pointer select-none"
                      onClick={() => {
                        if (!form.payDeposit) {
                          setVerifiedGiftCard(null)
                          setGiftCardNumber('')
                          setForm(prev => ({ ...prev, payDeposit: true, useGiftCard: false }))
                        } else {
                          setForm(prev => ({ ...prev, payDeposit: false }))
                        }
                      }}
                    >
                      <input 
                        type="checkbox" 
                        name="payDeposit" 
                        checked={form.payDeposit} 
                        onChange={handleChange}
                        className="w-5 h-5 text-purple-500 rounded focus:ring-2 focus:ring-purple-400 pointer-events-auto"
                      />
                      <label className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                        Pay deposit now
                      </label>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-4 space-x-4">
                  {showCompleteDepositButton && (
                  <button 
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        
                        // Capture reference before any state changes
                        const ref = depositPaymentReference
                        
                        // Immediately stop loading and hide button
                        setProcessingPayment(false)
                        setShowCompleteDepositButton(false)
                        
                        // If no reference, just stop - payment was cancelled
                        if (!ref) {
                          toast.info('Payment cancelled. You can start a new booking.')
                          return
                        }
                        
                        // Then trigger verification with captured reference
                        setTimeout(() => {
                          handleCompleteDepositPayment(ref)
                        }, 100)
                      }}
                      className="inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 hover:from-yellow-500 hover:via-yellow-600 hover:to-yellow-700 text-white font-bold py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 animate-pulse border-2 border-yellow-300"
                    >
                      <CheckCircle2 className="w-5 h-5" />
                      <span>Confirm Payment/Booking</span>
                    </button>
                  )}
                  <button 
                    type={processingPayment && !showCompleteDepositButton ? "button" : "submit"}
                    onClick={(e) => {
                      // If button is stuck in loading (processing but no deposit button shown), allow click to reset
                      if (processingPayment && !showCompleteDepositButton) {
                        e.preventDefault()
                        e.stopPropagation()
                        // Force immediate reset - use setTimeout to ensure state updates
                        setTimeout(() => {
                          setProcessingPayment(false)
                          setIsSubmittingBooking(false)
                          setShowCompleteDepositButton(false)
                          setDepositPaymentReference('')
                        }, 0)
                        toast.info('Payment cancelled. You can try again.')
                        return false
                      }
                      // Normal form submission continues
                    }}
                    disabled={showCompleteDepositButton || (isSubmittingBooking && !processingPayment)}
                    className="inline-flex items-center justify-center space-x-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    style={{ cursor: processingPayment && !showCompleteDepositButton ? 'pointer' : undefined }}
                  >
                    {processingPayment || isSubmittingBooking ? (
                      <>
                        <span>Processing...</span>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      </>
                    ) : (
                      <>
                        <span>{form.payDeposit && !form.useGiftCard && depositAmount > 0 ? 'Proceed to Payment' : 'Confirm Booking'}</span>
                    <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Legal modal for Terms / Privacy (non-blocking) */}
      {legalModalType && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-800">
                {legalModalType === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
              </h2>
              <button
                type="button"
                onClick={() => setLegalModalType(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto bg-gray-50">
              <div className="p-4">
                {legalModalType === 'terms' ? <Terms /> : <Privacy />}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={() => setLegalModalType(null)}
                className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Gift Card Verification Modal */}
      {showGiftCardModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full border border-gray-200">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
                <CreditCard className="w-5 h-5 text-purple-500" />
                <span>Verify Gift Card</span>
              </h3>
              <button 
                onClick={() => {
                  setShowGiftCardModal(false)
                  setGiftCardNumber('')
                  setGiftCardError('')
                  if (!verifiedGiftCard) {
                    setForm(prev => ({ ...prev, useGiftCard: false }))
                  }
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gift Card Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={giftCardNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 12)
                    setGiftCardNumber(value)
                    setGiftCardError('')
                  }}
                  placeholder="Enter 12-digit card number"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-purple-400 transition-all"
                  maxLength={12}
                  disabled={verifying}
                />
                <p className="text-xs text-gray-500 mt-1">Enter the 12-digit number from your gift card</p>
              </div>

              {giftCardError && (
                <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{giftCardError}</p>
                </div>
              )}

              {verifiedGiftCard && (
                <div className="flex items-start space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-700">
                    <p className="font-semibold">Gift card verified!</p>
                    <p className="mt-1">
                      {verifiedGiftCard.type === 'session' 
                        ? 'This session gift card will cover your booking.'
                        : `Balance: GHS ${Number(verifiedGiftCard.balance || 0).toFixed(2)}${verifiedGiftCard.balanceToPay ? ` | Balance to pay: GHS ${verifiedGiftCard.balanceToPay.toFixed(2)}` : ''}`
                      }
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowGiftCardModal(false)
                    setGiftCardNumber('')
                    setGiftCardError('')
                    if (!verifiedGiftCard) {
                      setForm(prev => ({ ...prev, useGiftCard: false }))
                    }
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  disabled={verifying}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={verifyGiftCard}
                  disabled={verifying || giftCardNumber.length !== 12}
                  className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {verifying ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Verify and Book</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Balance Payment Confirmation Modal */}
      {showBalancePaymentModal && balancePaymentInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-orange-500" />
                <span>Insufficient Gift Card Balance</span>
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Gift Card Balance:</span>
                  <span className="text-sm font-semibold text-gray-800">GHS {balancePaymentInfo.cardBalance.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Service Price:</span>
                  <span className="text-sm font-semibold text-gray-800">GHS {balancePaymentInfo.servicePrice.toFixed(2)}</span>
                </div>
                <div className="border-t border-orange-200 pt-3 mt-3">
                  <div className="flex items-center justify-between">
                    <span className="text-base font-semibold text-gray-800">Balance to Pay:</span>
                    <span className="text-lg font-bold text-orange-600">GHS {balancePaymentInfo.balance.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-gray-600">
                Your gift card balance doesn't cover the full service price. Do you agree to pay the balance after service?
              </p>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowBalancePaymentModal(false)
                    setBalancePaymentInfo(null)
                    setGiftCardError('Booking cancelled. Please use a different payment method.')
                    setShowGiftCardModal(true)
                  }}
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    if (processingAgreement) return
                    
                    setProcessingAgreement(true)
                    try {
                      setVerifiedGiftCard({ ...balancePaymentInfo.giftCard, balanceToPay: balancePaymentInfo.balance })
                      setShowBalancePaymentModal(false)
                      setBalancePaymentInfo(null)
                      toast.success('Gift card verified! Balance will be paid after service.')
                    } catch (error) {
                      console.error('Error processing agreement:', error)
                      toast.error('An error occurred. Please try again.')
                    } finally {
                      setProcessingAgreement(false)
                    }
                  }}
                  disabled={processingAgreement}
                  className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold rounded-lg transition-all flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processingAgreement ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Agree & Continue</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Book
